/**
 * Master Bot - Subscription Page
 * Subscription plan management
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTelegram } from '../../hooks/useTelegram';

const CurrentPlan = styled(Card)`
  background: linear-gradient(135deg, var(--accent-green) 0%, #45a049 100%);
  color: white;
  margin-bottom: var(--spacing-lg);
`;

const PlanTitle = styled.h2`
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  margin-bottom: var(--spacing-sm);
`;

const PlanDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--spacing-md);
`;

const PlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const PlanPrice = styled.div`
  font-size: var(--font-xxl);
  font-weight: var(--font-bold);
`;

const PlanPeriod = styled.div`
  font-size: var(--font-sm);
  opacity: 0.9;
`;

const PlanExpiry = styled.div`
  font-size: var(--font-sm);
  opacity: 0.9;
  margin-top: var(--spacing-md);
`;

const SectionTitle = styled.h3`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin: var(--spacing-lg) 0 var(--spacing-md);
`;

const PlanCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const PlanCard = styled(Card)<{ recommended?: boolean }>`
  position: relative;
  border: 2px solid ${({ recommended }) => (recommended ? 'var(--accent-green)' : 'transparent')};
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: -12px;
  right: var(--spacing-lg);
  background: var(--accent-green);
  color: white;
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
`;

const PlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-md);
`;

const PlanName = styled.h4`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const PlanCost = styled.div`
  text-align: right;
`;

const Price = styled.div`
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--accent-green);
`;

const OriginalPrice = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  text-decoration: line-through;
  margin-top: var(--spacing-xs);
`;

const Savings = styled.div`
  font-size: var(--font-sm);
  color: var(--accent-green);
  margin-top: var(--spacing-xs);
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: var(--spacing-md) 0;
`;

const Feature = styled.li`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  padding: var(--spacing-xs) 0;
  padding-left: var(--spacing-md);
  position: relative;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--accent-green);
  }
`;

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const { showBackButton } = useTelegram();
  const [currentPlan] = useState({
    name: 'Free Trial',
    price: '$0',
    period: '7 days remaining',
    expiry: 'Expires on Oct 13, 2025',
  });

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly Plan',
      price: '$5',
      period: '/month',
      features: ['All features included', 'Unlimited products', '24/7 support', 'Cancel anytime'],
    },
    {
      id: '6month',
      name: '6-Month Plan',
      price: '$27',
      period: 'for 6 months',
      originalPrice: '$30',
      savings: 'Save $3 (10% off)',
      recommended: true,
      features: [
        'All features included',
        'Unlimited products',
        '24/7 support',
        '10% discount',
        'Priority support',
      ],
    },
    {
      id: 'annual',
      name: 'Annual Plan',
      price: '$48',
      period: '/year',
      originalPrice: '$60',
      savings: 'Save $12 (20% off)',
      features: [
        'All features included',
        'Unlimited products',
        '24/7 support',
        '20% discount',
        'Priority support',
        'Early access to new features',
      ],
    },
  ];

  useEffect(() => {
    showBackButton(() => navigate('/master/dashboard'));
  }, [showBackButton, navigate]);

  return (
    <Layout header={<Navbar title="Subscription" showBack />}>
      <CurrentPlan>
        <PlanTitle>Current Plan</PlanTitle>
        <PlanDetails>
          <PlanInfo>
            <PlanPrice>{currentPlan.price}</PlanPrice>
            <PlanPeriod>{currentPlan.name}</PlanPeriod>
          </PlanInfo>
        </PlanDetails>
        <PlanExpiry>{currentPlan.expiry}</PlanExpiry>
      </CurrentPlan>

      <SectionTitle>Upgrade Your Plan</SectionTitle>
      <PlanCards>
        {plans.map((plan) => (
          <PlanCard key={plan.id} recommended={plan.recommended}>
            {plan.recommended && <RecommendedBadge>RECOMMENDED</RecommendedBadge>}
            <PlanHeader>
              <PlanName>{plan.name}</PlanName>
              <PlanCost>
                <Price>
                  {plan.price}
                  <span style={{ fontSize: 'var(--font-sm)', fontWeight: 'normal' }}>{plan.period}</span>
                </Price>
                {plan.originalPrice && <OriginalPrice>{plan.originalPrice}</OriginalPrice>}
                {plan.savings && <Savings>{plan.savings}</Savings>}
              </PlanCost>
            </PlanHeader>
            <PlanFeatures>
              {plan.features.map((feature, index) => (
                <Feature key={index}>{feature}</Feature>
              ))}
            </PlanFeatures>
            <Button variant={plan.recommended ? 'primary' : 'secondary'} fullWidth>
              Select Plan
            </Button>
          </PlanCard>
        ))}
      </PlanCards>
    </Layout>
  );
};

export default Subscription;
